import { useState, useMemo, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { ProductSelector } from "@/components/ProductSelector";
import { PeriodFilter, Period } from "@/components/PeriodFilter";
import { useMetrics } from "@/hooks/useMetrics";
import { useSubscriptionLimits } from "@/hooks/useSubscriptionLimits";
import { useProduct } from "@/contexts/ProductContext";
import { MetricCardWithDetails } from "@/components/metrics/MetricCardWithDetails";
import { MetricsSummaryTable } from "@/components/metrics/MetricsSummaryTable";
import { MetricsDetailedTable } from "@/components/metrics/MetricsDetailedTable";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, LayoutGrid, Table as TableIcon, List, TrendingUp } from "lucide-react";
import { subDays, format } from "date-fns";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

type ViewMode = 'cards' | 'table' | 'detailed';

const formatDateRange = (startDate: Date, endDate: Date) => {
  return `${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`;
};

const Metrics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<Period>("30d");
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const { selectedProduct } = useProduct();
  const { getBasicMetrics, getAdvancedMetricsConfig, isLoading: planLoading } = useSubscriptionLimits();

  useEffect(() => {
    document.title = 'Dashi | Métricas';
  }, []);

  // Calcular datas baseado no período
  const dateRange = useMemo(() => {
    const today = new Date();
    const yesterday = subDays(today, 1);
    let startDate: Date;
    let endDate: Date;

    switch (selectedPeriod) {
      case "7d":
        // Últimos 7 dias: De (ontem - 6 dias) até ontem
        endDate = yesterday;
        startDate = subDays(yesterday, 6);
        break;
      case "30d":
        // Mês Atual: Do dia 1º do mês vigente até ontem
        endDate = yesterday;
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case "90d":
        // Último Trimestre: Os últimos 3 meses completos
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        const threeMonthsAgoStart = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        endDate = lastMonthEnd;
        startDate = threeMonthsAgoStart;
        break;
      default:
        endDate = yesterday;
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    return { startDate, endDate };
  }, [selectedPeriod]);

  const { data: metricsData, isLoading: metricsLoading } = useMetrics({
    productId: selectedProduct?.id,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const allowedBasicMetrics = getBasicMetrics();
  const advancedConfig = getAdvancedMetricsConfig();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  // Preparar dados para as visualizações de tabela - SEMPRE chamar hooks
  const basicMetricsData = useMemo(() => {
    if (!metricsData) return [];
    
    const metrics = [];
    
    if (allowedBasicMetrics.includes('MRR')) {
      metrics.push({
        name: 'MRR',
        value: formatCurrency(metricsData.mrr),
        change: metricsData.mrrChange,
        trend: metricsData.mrrChange > 0 ? 'up' as const : metricsData.mrrChange < 0 ? 'down' as const : 'neutral' as const,
        metricType: 'other' as const,
      });
    }
    
    if (allowedBasicMetrics.includes('ARR')) {
      metrics.push({
        name: 'ARR',
        value: formatCurrency(metricsData.arr),
        change: metricsData.arrChange,
        trend: metricsData.arrChange > 0 ? 'up' as const : metricsData.arrChange < 0 ? 'down' as const : 'neutral' as const,
        metricType: 'other' as const,
      });
    }
    
    if (allowedBasicMetrics.includes('Taxa de Churn')) {
      metrics.push({
        name: 'Taxa de Churn',
        value: formatPercent(metricsData.churn),
        change: metricsData.churnChange,
        trend: metricsData.churnChange < 0 ? 'up' as const : metricsData.churnChange > 0 ? 'down' as const : 'neutral' as const,
        metricType: 'churn' as const,
      });
    }
    
    if (allowedBasicMetrics.includes('LTV')) {
      metrics.push({
        name: 'LTV (Lifetime Value)',
        value: formatCurrency(metricsData.ltv),
        metricType: 'other' as const,
      });
    }
    
    if (allowedBasicMetrics.includes('Receita Total')) {
      metrics.push({
        name: 'Receita Total',
        value: formatCurrency(metricsData.totalRevenue),
        change: metricsData.revenueChange,
        trend: metricsData.revenueChange > 0 ? 'up' as const : metricsData.revenueChange < 0 ? 'down' as const : 'neutral' as const,
        metricType: 'other' as const,
      });
    }
    
    if (allowedBasicMetrics.includes('Ticket Médio')) {
      metrics.push({
        name: 'Ticket Médio',
        value: formatCurrency(metricsData.averageTicket),
        metricType: 'other' as const,
      });
    }
    
    return metrics;
  }, [metricsData, allowedBasicMetrics]);

  const detailedMetricsData = useMemo(() => {
    if (!metricsData) return [];
    
    const metrics = [];
    
    if (allowedBasicMetrics.includes('MRR')) {
      metrics.push({
        title: 'MRR',
        value: formatCurrency(metricsData.mrr),
        change: metricsData.mrrChange,
        trend: metricsData.mrrChange > 0 ? 'up' as const : metricsData.mrrChange < 0 ? 'down' as const : 'neutral' as const,
        metricType: 'mrr' as const,
        filters: dateRange,
      });
    }
    
    if (allowedBasicMetrics.includes('ARR')) {
      metrics.push({
        title: 'ARR',
        value: formatCurrency(metricsData.arr),
        change: metricsData.arrChange,
        trend: metricsData.arrChange > 0 ? 'up' as const : metricsData.arrChange < 0 ? 'down' as const : 'neutral' as const,
        metricType: 'arr' as const,
        filters: dateRange,
      });
    }
    
    if (allowedBasicMetrics.includes('Taxa de Churn')) {
      metrics.push({
        title: 'Taxa de Churn',
        value: formatPercent(metricsData.churn),
        change: metricsData.churnChange,
        trend: metricsData.churnChange < 0 ? 'up' as const : metricsData.churnChange > 0 ? 'down' as const : 'neutral' as const,
        metricType: 'churn' as const,
        filters: dateRange,
      });
    }
    
    if (allowedBasicMetrics.includes('LTV')) {
      metrics.push({
        title: 'LTV (Lifetime Value)',
        value: formatCurrency(metricsData.ltv),
        metricType: 'ltv' as const,
        filters: dateRange,
      });
    }
    
    if (allowedBasicMetrics.includes('Receita Total')) {
      metrics.push({
        title: 'Receita Total',
        value: formatCurrency(metricsData.totalRevenue),
        change: metricsData.revenueChange,
        trend: metricsData.revenueChange > 0 ? 'up' as const : metricsData.revenueChange < 0 ? 'down' as const : 'neutral' as const,
        metricType: 'revenue' as const,
        filters: dateRange,
      });
    }
    
    if (allowedBasicMetrics.includes('Ticket Médio')) {
      metrics.push({
        title: 'Ticket Médio',
        value: formatCurrency(metricsData.averageTicket),
        metricType: 'average_ticket' as const,
        filters: dateRange,
      });
    }
    
    return metrics;
  }, [metricsData, allowedBasicMetrics, dateRange]);

  const advancedMetricsData = useMemo(() => {
    if (!metricsData || !advancedConfig.enabled || advancedConfig.metrics.length === 0) return [];
    
    const metrics = [];
    
    if (advancedConfig.metrics.includes('CAC')) {
      metrics.push({
        name: 'CAC (Custo de Aquisição)',
        value: formatCurrency(metricsData.cac),
        metricType: 'other' as const,
      });
    }
    
    if (advancedConfig.metrics.includes('Ticket Médio')) {
      metrics.push({
        name: 'Ticket Médio',
        value: formatCurrency(metricsData.averageTicket),
        metricType: 'other' as const,
      });
    }
    
    if (advancedConfig.metrics.includes('Churn Detalhado')) {
      metrics.push({
        name: 'Churn Detalhado',
        value: formatPercent(metricsData.churn),
        change: metricsData.churnChange,
        trend: metricsData.churnChange < 0 ? 'up' as const : metricsData.churnChange > 0 ? 'down' as const : 'neutral' as const,
        metricType: 'churn' as const,
      });
    }
    
    return metrics;
  }, [metricsData, advancedConfig]);

  const advancedDetailedMetricsData = useMemo(() => {
    if (!metricsData || !advancedConfig.enabled || advancedConfig.metrics.length === 0) return [];
    
    const metrics = [];
    
    if (advancedConfig.metrics.includes('CAC')) {
      metrics.push({
        title: 'CAC (Custo de Aquisição)',
        value: formatCurrency(metricsData.cac),
        metricType: 'cac' as const,
        filters: dateRange,
      });
    }
    
    if (advancedConfig.metrics.includes('Ticket Médio')) {
      metrics.push({
        title: 'Ticket Médio',
        value: formatCurrency(metricsData.averageTicket),
        metricType: 'average_ticket' as const,
        filters: dateRange,
      });
    }
    
    if (advancedConfig.metrics.includes('Churn Detalhado')) {
      metrics.push({
        title: 'Churn Detalhado',
        value: formatPercent(metricsData.churn),
        change: metricsData.churnChange,
        trend: metricsData.churnChange < 0 ? 'up' as const : metricsData.churnChange > 0 ? 'down' as const : 'neutral' as const,
        metricType: 'churn' as const,
        filters: dateRange,
      });
    }
    
    return metrics;
  }, [metricsData, advancedConfig, dateRange]);

  if (planLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="space-y-6">
          <Skeleton className="h-20 w-full" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="container mx-auto py-6 space-y-6">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <TrendingUp className="h-8 w-8" />
              Análise de Métricas
            </h1>
            <p className="text-muted-foreground mt-1">
              Análise detalhada do desempenho do seu negócio
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <ProductSelector />
            <PeriodFilter 
              selectedPeriod={selectedPeriod}
              onPeriodChange={setSelectedPeriod}
            />
            
            <div className="ml-auto">
              <ToggleGroup type="single" value={viewMode} onValueChange={(value) => value && setViewMode(value as ViewMode)} className="rounded-lg p-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem 
                      value="cards" 
                      aria-label="Visualização em cards"
                      className="data-[state=on]:gradient-primary data-[state=on]:text-primary-foreground data-[state=off]:bg-transparent data-[state=off]:hover:bg-muted rounded-md transition-smooth"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Visualização em cards</p>
                  </TooltipContent>
                </Tooltip>
                
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem 
                      value="table" 
                      aria-label="Tabela resumida"
                      className="data-[state=on]:gradient-primary data-[state=on]:text-primary-foreground data-[state=off]:bg-transparent data-[state=off]:hover:bg-muted rounded-md transition-smooth"
                    >
                      <TableIcon className="h-4 w-4" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Tabela resumida</p>
                  </TooltipContent>
                </Tooltip>
                
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem 
                      value="detailed" 
                      aria-label="Tabela detalhada"
                      className="data-[state=on]:gradient-primary data-[state=on]:text-primary-foreground data-[state=off]:bg-transparent data-[state=off]:hover:bg-muted rounded-md transition-smooth"
                    >
                      <List className="h-4 w-4" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Tabela detalhada</p>
                  </TooltipContent>
                </Tooltip>
              </ToggleGroup>
            </div>
          </div>

          {/* Renderização condicional baseada no viewMode */}
          {allowedBasicMetrics.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Seu plano não possui métricas básicas configuradas. Entre em contato com o suporte.
              </AlertDescription>
            </Alert>
          ) : metricsLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : viewMode === 'table' ? (
            <>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-semibold">Métricas Básicas</h2>
                  <span className="text-sm text-muted-foreground">
                    {formatDateRange(dateRange.startDate, dateRange.endDate)}
                  </span>
                </div>
                <MetricsSummaryTable metrics={basicMetricsData} />
              </div>
              
              {advancedMetricsData.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-semibold">Métricas Avançadas</h2>
                    <span className="text-sm text-muted-foreground">
                      {formatDateRange(dateRange.startDate, dateRange.endDate)}
                    </span>
                  </div>
                  <MetricsSummaryTable metrics={advancedMetricsData} />
                </div>
              )}
            </>
          ) : viewMode === 'detailed' ? (
            <>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-semibold">Métricas Básicas</h2>
                  <span className="text-sm text-muted-foreground">
                    {formatDateRange(dateRange.startDate, dateRange.endDate)}
                  </span>
                </div>
                <MetricsDetailedTable metrics={detailedMetricsData} />
              </div>
              
              {advancedDetailedMetricsData.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-semibold">Métricas Avançadas</h2>
                    <span className="text-sm text-muted-foreground">
                      {formatDateRange(dateRange.startDate, dateRange.endDate)}
                    </span>
                  </div>
                  <MetricsDetailedTable metrics={advancedDetailedMetricsData} />
                </div>
              )}
            </>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold">Métricas Básicas</h2>
                <span className="text-sm text-muted-foreground">
                  {formatDateRange(dateRange.startDate, dateRange.endDate)}
                </span>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {allowedBasicMetrics.includes('MRR') && metricsData && (
                  <MetricCardWithDetails
                    title="MRR"
                    value={formatCurrency(metricsData.mrr)}
                    change={metricsData.mrrChange}
                    trend={metricsData.mrrChange > 0 ? 'up' : metricsData.mrrChange < 0 ? 'down' : 'neutral'}
                    metricType="mrr"
                    filters={dateRange}
                  />
                )}

                {allowedBasicMetrics.includes('ARR') && metricsData && (
                  <MetricCardWithDetails
                    title="ARR"
                    value={formatCurrency(metricsData.arr)}
                    change={metricsData.arrChange}
                    trend={metricsData.arrChange > 0 ? 'up' : metricsData.arrChange < 0 ? 'down' : 'neutral'}
                    metricType="arr"
                    filters={dateRange}
                  />
                )}

                {allowedBasicMetrics.includes('Taxa de Churn') && metricsData && (
                  <MetricCardWithDetails
                    title="Taxa de Churn"
                    value={formatPercent(metricsData.churn)}
                    change={metricsData.churnChange}
                    trend={metricsData.churnChange < 0 ? 'up' : metricsData.churnChange > 0 ? 'down' : 'neutral'}
                    metricType="churn"
                    filters={dateRange}
                  />
                )}

                {allowedBasicMetrics.includes('LTV') && metricsData && (
                  <MetricCardWithDetails
                    title="LTV (Lifetime Value)"
                    value={formatCurrency(metricsData.ltv)}
                    metricType="ltv"
                    filters={dateRange}
                  />
                )}

                {allowedBasicMetrics.includes('Receita Total') && metricsData && (
                  <MetricCardWithDetails
                    title="Receita Total"
                    value={formatCurrency(metricsData.totalRevenue)}
                    change={metricsData.revenueChange}
                    trend={metricsData.revenueChange > 0 ? 'up' : metricsData.revenueChange < 0 ? 'down' : 'neutral'}
                    metricType="revenue"
                    filters={dateRange}
                  />
                )}

                {allowedBasicMetrics.includes('Ticket Médio') && metricsData && (
                  <MetricCardWithDetails
                    title="Ticket Médio"
                    value={formatCurrency(metricsData.averageTicket)}
                    metricType="average_ticket"
                    filters={dateRange}
                  />
                )}
              </div>

              {/* Métricas Avançadas */}
              {advancedConfig.enabled && advancedConfig.metrics.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-semibold">Métricas Avançadas</h2>
                    <span className="text-sm text-muted-foreground">
                      {formatDateRange(dateRange.startDate, dateRange.endDate)}
                    </span>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {advancedConfig.metrics.includes('CAC') && metricsData && (
                      <MetricCardWithDetails
                        title="CAC (Custo de Aquisição)"
                        value={formatCurrency(metricsData.cac)}
                        metricType="cac"
                        filters={dateRange}
                      />
                    )}

                    {advancedConfig.metrics.includes('Ticket Médio') && metricsData && (
                      <MetricCardWithDetails
                        title="Ticket Médio"
                        value={formatCurrency(metricsData.averageTicket)}
                        metricType="average_ticket"
                        filters={dateRange}
                      />
                    )}

                    {advancedConfig.metrics.includes('Churn Detalhado') && metricsData && (
                      <MetricCardWithDetails
                        title="Churn Detalhado"
                        value={formatPercent(metricsData.churn)}
                        change={metricsData.churnChange}
                        trend={metricsData.churnChange < 0 ? 'up' : metricsData.churnChange > 0 ? 'down' : 'neutral'}
                        metricType="churn"
                        filters={dateRange}
                      />
                    )}
                  </div>

                  {advancedConfig.metrics.some(m => 
                    ['Cohort Analysis', 'Previsão de Receita', 'Análise de Retenção', 'ROI por Canal', 'Métricas de Engajamento'].includes(m)
                  ) && (
                    <Card className="p-6 mt-4">
                      <h3 className="text-lg font-semibold mb-2">Métricas Adicionais</h3>
                      <p className="text-muted-foreground text-sm">
                        As seguintes métricas avançadas estão habilitadas em seu plano mas ainda não possuem visualização implementada:
                      </p>
                      <ul className="list-disc list-inside mt-2 text-sm text-muted-foreground">
                        {advancedConfig.metrics.filter(m => 
                          ['Cohort Analysis', 'Previsão de Receita', 'Análise de Retenção', 'ROI por Canal', 'Métricas de Engajamento'].includes(m)
                        ).map(metric => (
                          <li key={metric}>{metric}</li>
                        ))}
                      </ul>
                    </Card>
                  )}
                </div>
              )}

              {!advancedConfig.enabled && (
                <Card className="p-6 mt-8">
                  <h3 className="text-lg font-semibold mb-2">Métricas Avançadas</h3>
                  <p className="text-muted-foreground">
                    Faça upgrade do seu plano para ter acesso a métricas avançadas como CAC, Cohort Analysis, 
                    Previsão de Receita e muito mais.
                  </p>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Metrics;
